<template>
  <div class="layer-widget-panel scrollbar">
    <div class="measurements-container">
      <div class="measurements">
        <div class="form-group hidden position">
          <div class="form-row">
            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">X</span>
              </div>
              <input
                type="number"
                class="form-control"
                id="layer-x"
                v-model="xPosition"
                :disabled="isWidgetLocked"
              />
            </div>

            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">Y</span>
              </div>
              <input
                type="number"
                class="form-control"
                v-model="yPosition"
                :disabled="isWidgetLocked"
              />
            </div>
          </div>
        </div>

        <div class="form-group hidden size">
          <div class="form-row">
            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">W</span>
              </div>
              <input
                type="number"
                class="form-control"
                id="layer-width"
                v-model="width"
                :disabled="isWidgetLocked"
              />
            </div>

            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">H</span>
              </div>
              <input
                type="number"
                class="form-control"
                v-model="height"
                :disabled="isWidgetLocked"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="extra-icons">
        <LockIcon :locked="isWidgetLocked" />
        <VisibilityIcon :isVisible="isWidgetVisible" @clicked="updateVisibility" />
        <PixelIcon :usePixels="usePixels" @clicked="changeValuesMode" />
      </div>
    </div>

    <div class="form-group template-input name">
      <label for="template-name">Widget Name</label>
      <input
        class="form-control"
        type="text"
        id="template-name"
        maxlength="40"
        placeholder="Name"
        :disabled="isWidgetLockedByOverride"
        v-model="value.object.name"
      />
    </div>

    <div class="form-group template-input">
      <label for="widget-type">Widget Type</label>
      <select class="form-control" id="widget-type" v-model="value.object.date.type">
        <option :value="DATE_WIDGET_TYPE.TODAY">Today</option>
        <option :value="DATE_WIDGET_TYPE.WEEK">Week</option>
      </select>
    </div>

    <div class="form-group template-input">
      <label for="language">Language</label>
      <select class="form-control" id="language" v-model="value.object.date.language">
        <option value="en">English</option>
        <option value="sv">Swedish</option>
      </select>
    </div>

    <div class="title-row">
      <div class="font-container form-group layout-input">
        <label for="font">Font</label>
        <FontSelector
          :value="value.object.date.textFont"
          :fonts="userFonts"
          @update:value="updateDateFont"
        ></FontSelector>
      </div>

      <div class="template-input form-group">
        <label for="text-size">Text Size</label>

        <input
          type="number"
          class="form-control"
          id="text-size"
          :disabled="isWidgetLockedByOverride"
          min="4"
          max="120"
          v-model="textSize"
        />
      </div>

      <div class="color-section">
        <ColorPicker v-model="value.object.date.fontColor" :leftPosition="-210">
          <div class="color-button" title="Background Color">
            <i class="material-icons-outlined align-middle">text_fields</i>

            <div class="color-bar" :style="{ backgroundColor: value.object.date.fontColor }"></div>
          </div>
        </ColorPicker>

        <ColorPicker
          v-if="value.object.date.type === DATE_WIDGET_TYPE.WEEK"
          v-model="value.object.date.highlightColor"
          :leftPosition="-260"
        >
          <div class="color-button" title="Highlight Color">
            <i class="material-icons-outlined align-middle">highlight</i>
            <div
              class="color-bar"
              :style="{ backgroundColor: value.object.date.highlightColor }"
            ></div>
          </div>
        </ColorPicker>
      </div>
    </div>

    <div
      v-if="value.object.date.type === DATE_WIDGET_TYPE.WEEK"
      class="template-input form-group w-50"
    >
      <label for="week-text-size">Week Text Size</label>

      <input
        type="number"
        class="form-control"
        id="week-text-size"
        :disabled="isWidgetLockedByOverride"
        min="4"
        max="120"
        v-model="weekTextSize"
      />
    </div>

    <div v-if="value.object.date.type === DATE_WIDGET_TYPE.TODAY" class="form-group template-input">
      <label for="date-format">Date Format</label>
      <select class="form-control" id="date-format" v-model="value.object.date.format">
        <option :value="DATE_FORMAT.EU_FORMAT">
          {{ formatCurrentDate(DATE_FORMAT.EU_FORMAT) }}
        </option>
        <option :value="DATE_FORMAT.EU_FORMAT_DASH">
          {{ formatCurrentDate(DATE_FORMAT.EU_FORMAT_DASH) }}
        </option>
        <option :value="DATE_FORMAT.EU_FORMAT_DOT">
          {{ formatCurrentDate(DATE_FORMAT.EU_FORMAT_DOT) }}
        </option>
        <option :value="DATE_FORMAT.US_FORMAT">
          {{ formatCurrentDate(DATE_FORMAT.US_FORMAT) }}
        </option>
        <option :value="DATE_FORMAT.US_FORMAT_DASH">
          {{ formatCurrentDate(DATE_FORMAT.US_FORMAT_DASH) }}
        </option>
        <option :value="DATE_FORMAT.US_FORMAT_DOT">
          {{ formatCurrentDate(DATE_FORMAT.US_FORMAT_DOT) }}
        </option>
        <option :value="DATE_FORMAT.US_LONG">
          {{ formatCurrentDate(DATE_FORMAT.US_LONG) }}
        </option>
        <option :value="DATE_FORMAT.EU_LONG">
          {{ formatCurrentDate(DATE_FORMAT.EU_LONG) }}
        </option>
        <option :value="DATE_FORMAT.WEEKDAY">{{ formatCurrentDate(DATE_FORMAT.WEEKDAY) }}</option>
        <option :value="DATE_FORMAT.MONTH">{{ formatCurrentDate(DATE_FORMAT.MONTH) }}</option>
        <option :value="DATE_FORMAT.NO_DAY_LONG">
          {{ formatCurrentDate(DATE_FORMAT.NO_DAY_LONG) }}
        </option>
        <option :value="DATE_FORMAT.YEAR">{{ formatCurrentDate(DATE_FORMAT.YEAR) }}</option>
        <option :value="DATE_FORMAT.DAY">{{ formatCurrentDate(DATE_FORMAT.DAY) }}</option>
        <option :value="DATE_FORMAT.DAY_NAME">
          {{ formatCurrentDate(DATE_FORMAT.DAY_NAME) }}
        </option>
      </select>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import moment from 'moment-timezone';

  import widgetMixin from '@/components/templates/editor/settingPanel/widgetMixin';
  import LockIcon from '@/components/layoutDesigner/editor/settingPanel/LockIcon.vue';
  import VisibilityIcon from '@/components/layoutDesigner/editor/settingPanel/VisibilityIcon.vue';
  import PixelIcon from '@/components/layoutDesigner/editor/settingPanel/PixelIcon.vue';
  import FontSelector from '@/components/common/fontSelector/FontSelector.vue';
  import ColorPicker from '@/components/ColorPicker.vue';

  import { DATE_WIDGET_TYPE, DATE_FORMAT } from '@/constant/dateWidget';
  import { restrictToBounds } from '@/helpers/draggable';

  export default {
    name: 'DateWidgetPanel',

    components: {
      LockIcon,
      VisibilityIcon,
      PixelIcon,
      FontSelector,
      ColorPicker,
    },

    mixins: [widgetMixin],

    data() {
      return {
        DATE_WIDGET_TYPE,
        DATE_FORMAT,
      };
    },

    computed: {
      ...mapGetters(['userFonts']),

      formattedCurrentDate() {
        const today = new Date();
        moment.locale(this.language);
        const date = moment(today);

        return {
          usFormat: date.format(DATE_FORMAT.US_FORMAT),
          euFormat: date.format(DATE_FORMAT.EU_FORMAT),
          isoFormat: date.format(DATE_FORMAT.ISO_FORMAT),
          dotFormat: date.format(DATE_FORMAT.DOT_FORMAT),
          usLong: date.format(DATE_FORMAT.US_LONG),
          euLong: date.format(DATE_FORMAT.EU_LONG),
          weekday: date.format(DATE_FORMAT.WEEKDAY),
        };
      },

      textSize: {
        get() {
          return this.value.object.date.textSize;
        },
        set(value) {
          const textSize = restrictToBounds(value, 1, 100);
          this.$set(this.value.object.date, 'textSize', Number(textSize));
        },
      },

      weekTextSize: {
        get() {
          return this.value.object.date.weekTextSize;
        },
        set(value) {
          const weekTextSize = restrictToBounds(value, 1, 100);
          this.$set(this.value.object.date, 'weekTextSize', Number(weekTextSize));
        },
      },
    },

    methods: {
      updateDateFont(newFont) {
        this.value.object.date.textFont = newFont;
      },

      formatCurrentDate(format) {
        const today = new Date();
        moment.locale(this.language);
        const date = moment(today);

        return date.format(format);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .vertical-padding {
    margin-top: 10px;
    width: 40%;
  }

  .layer-widget-panel {
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    margin-right: 0px;
    padding-right: 10px;

    .sub-section-title {
      padding-top: 4px;
      font-weight: 500;
      font-size: 14px;
      line-height: 24px;
      width: 100%;
    }
  }

  .picker {
    position: absolute;
    top: 40px;
    right: 0;
    z-index: 20;
  }

  .time-container {
    display: flex;
    gap: 16px;
  }

  .section-title {
    font-weight: bold;
    padding-top: 16px;
    font-size: 16px;
    line-height: 24px;
    margin-bottom: 10px;
    color: $tableText;
  }

  .buttons-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .slider-container {
    width: 100%;
    padding-right: 16px;
  }

  .measurements-container {
    padding-top: 6px;
  }

  .color-section {
    display: flex;
    gap: 10px;
    align-items: end;
    position: relative;
  }

  .title-row {
    display: grid;
    position: relative;
    gap: 10px;
    margin: 0 0 1rem;

    .form-group {
      margin-bottom: 0;
    }
  }

  .title-row {
    grid-template-columns: 120px 70px 90px;
  }
</style>
